import React, { useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Button, Popover, Row } from "antd";
import styled from "styled-components";
import { useI18n } from "../hooks";
import { copyToClipboard } from "../utils/clipboard";

interface Props extends React.ComponentProps<typeof QRCodeCanvas> {
  copyButton?: boolean;
  downloadButton?: boolean;
  downloadSize?: number;
  size?: number;
  trigger?: "click" | "hover" | "none";
}

const QRCode = ({
  value,
  size = 180,
  imageSettings,
  copyButton = false,
  downloadButton = false,
  downloadSize = 180,
  includeMargin,
  trigger = "hover",
  ...props
}: Props): React.ReactElement => {
  const qrRef = useRef<HTMLDivElement | null>(null);
  const translate = useI18n();

  const downloadQRCode = () => {
    if (qrRef.current) {
      const canvas = qrRef.current.querySelector("canvas");
      if (canvas) {
        const pngUrl = canvas
          .toDataURL("image/png", 1.0)
          .replace("image/png", "image/octet-stream");
        const downloadLink = document.createElement("a");
        downloadLink.href = pngUrl;
        downloadLink.download = "QR code.png";
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }
    } else {
      throw new Error("<canvas> not found in the DOM");
    }
  };

  return (
    <QrContainer>
      <Popover
        trigger={trigger}
        content={
          <a href={value} target="_blank" rel="noreferrer">
            {value}
          </a>
        }
      >
        <div ref={qrRef} className="qrcode-wrapper">
          <QRCodeCanvas
            style={{ width: size, height: size }}
            className="qrcode-canvas"
            value={value}
            size={downloadSize}
            includeMargin={includeMargin}
            imageSettings={imageSettings}
            {...props}
          />
        </div>
      </Popover>
      <ButtonsWrapper>
        {downloadButton && (
          <Button type="default" size="middle" onClick={downloadQRCode}>
            {translate("general.download-qr")}
          </Button>
        )}
        {copyButton && (
          <Button
            type="default"
            size="middle"
            onClick={() => copyToClipboard(value)}
          >
            {translate("general.copy-link")}
          </Button>
        )}
      </ButtonsWrapper>
    </QrContainer>
  );
};

export default QRCode;

const QrContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  .qrcode-wrapper {
    display: flex;
    line-height: 0;
  }
`;

const ButtonsWrapper = styled(Row)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  justify-content: center;
`;
